// feature/notion-polish — reusable right-click context menu.
//
// Usage:
//   const { onContextMenu, menu } = useContextMenu(items);
//   <tr onContextMenu={onContextMenu}>...</tr>
//   {menu}
//
// `items` is an array of { label, icon?, onClick, destructive?, hidden? }
// or "divider" for a separator. `hidden:true` filters the item out at render
// time — useful for permission gating.
//
// Shift+right-click bypasses this menu and uses browser default.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MENU_W = 220;
const MENU_H_PER_ITEM = 32;

function safePos(x, y, h) {
  const vw = window.innerWidth, vh = window.innerHeight;
  return {
    x: Math.min(x, vw - MENU_W - 8),
    y: Math.min(y, vh - h - 8),
  };
}

export function useContextMenu(items) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const visible = (items || []).filter(it => !(it && it.hidden));
  const itemH = visible.reduce((acc, it) => acc + (it === "divider" ? 9 : MENU_H_PER_ITEM), 8);

  const onContextMenu = useCallback((e) => {
    if (e.shiftKey) return;        // escape hatch — let browser handle
    if (!visible.length) return;
    e.preventDefault();
    setPos(safePos(e.clientX, e.clientY, itemH));
  }, [visible.length, itemH]);

  useEffect(() => {
    if (!pos) return;
    function close(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      setPos(null);
    }
    function onKey(e) { if (e.key === "Escape") setPos(null); }
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [pos]);

  const menu = pos
    ? createPortal(
        <div ref={ref} className="ctx-menu" style={{ left: pos.x, top: pos.y, minWidth: MENU_W }}>
          {visible.map((it, i) => {
            if (it === "divider") return <div key={"d" + i} className="divider" />;
            return (
              <div
                key={it.label + i}
                className={"item" + (it.destructive ? " destructive" : "")}
                onClick={() => { setPos(null); it.onClick?.(); }}
              >
                {it.icon && <span style={{ width: 14, display: "inline-flex", justifyContent: "center" }}>{it.icon}</span>}
                <span>{it.label}</span>
              </div>
            );
          })}
        </div>,
        document.body
      )
    : null;

  return { onContextMenu, menu };
}
